import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useApi, useAuthApi } from "../../hooks/api/axiosInterceptor";

const Wrapper = styled.div`
    height: 100%;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 15px;
    margin-top: 20px;
`;

const LoginContainer = styled.div`
    width: 100%;
    max-width: 450px;
    margin: 20px 20px;
    padding: 35px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
`;

const FormGroup = styled.div`
    margin-bottom: 20px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
`;

const Input = styled.input`
    width: 100%;
    padding: 12px 15px;
    box-sizing: border-box;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    transition: border-color 0.3s;
    
    &:focus {
        border-color: #0066cc;
        outline: none;
    }
`;

const ErrorMessage = styled.p`
    color: #e74c3c;
    font-size: 14px;
    margin-top: 5px;
`;

const SubmitButton = styled.button`
    width: 100%;
    height: 48px;  
    padding: 12px 0;
    margin-top: 15px;
    background-color: #2B4F95;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
        background-color: #1a3a70;
    }
    
    &:disabled {
        background-color: #b3c1d1;
        cursor: not-allowed;
    }
`;

const HelpLinks = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    font-size: 14px;
`;

const StyledLink = styled(Link)`
    color: #666;
    text-decoration: none;
    
    &:hover {
        text-decoration: underline;
        color: #0066cc;
    }
`;

const SignUpButton = styled(Link)`
    display: block;
    width: 100%;
    height: 48px;  
    padding: 12px 0;
    margin-top: 15px;
    background-color: #f8f9fa;
    color: #333;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
        background-color: #e9ecef;
    }
`;

const Login = () => {
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // 입력 시 해당 필드의 에러 메시지 초기화
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.username.trim()) {
            newErrors.username = '아이디를 입력해주세요';
        }
        
        if (!formData.password) {
            newErrors.password = '비밀번호를 입력해주세요';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await useApi.post('/auth/login', {
                username: formData.username,
                password: formData.password
            });
            
            if (response.data.status === 'SUCCESS') {
                const userData = response.data.data.user;
                localStorage.setItem("access_token", response.data.data.access_token);
                localStorage.setItem('refresh_token', response.data.data.refresh_token);
                localStorage.setItem('user_role', userData.role);  // 여기에 'role' 값 저장

                navigate('/home');
            } else {
                setErrors({
                    form: response.data.message || '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.'
                });
            }
        } catch (error) {
            console.error('로그인 오류:', error);

            if (error.response?.data?.error_code === 'certify_required') {
                alert('관리자 승인이 필요합니다.');
            } else {
                setErrors({
                    form: '로그인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Wrapper>
            <LoginContainer>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label htmlFor="username">아이디</Label>
                        <Input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            placeholder="아이디를 입력하세요"
                        />
                        {errors.username && <ErrorMessage>{errors.username}</ErrorMessage>}
                    </FormGroup>
                    
                    <FormGroup>
                        <Label htmlFor="password">비밀번호</Label>
                        <Input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="비밀번호를 입력하세요"
                        />
                        {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
                    </FormGroup>
                    
                    {errors.form && <ErrorMessage>{errors.form}</ErrorMessage>}
                    
                    <ButtonContainer>
                        <SubmitButton type="submit" disabled={isLoading}>
                            {isLoading ? '로그인 중...' : '로그인'}
                        </SubmitButton>
                        <SignUpButton to="/intro/signup">
                            회원가입
                        </SignUpButton>
                    </ButtonContainer>
                </Form>
                
                <HelpLinks>
                    <StyledLink to="/intro/login/id-search">아이디 찾기</StyledLink>
                    <StyledLink to="/intro/login/pw-search">비밀번호 찾기</StyledLink>
                    <StyledLink to="/intro/login/pw-search-by-email">예비 이메일로 비밀번호 찾기</StyledLink>
                </HelpLinks>

            </LoginContainer>
        </Wrapper>
    );
};

export default Login;